import React, { useContext, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Button,
  IconButton,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from 'react-toastify';

// Import service
import projectSettings from '../../../services/project/projectSettingsService.js';

// Get Context
import ScriptContext from '../../../context/Script';

const PURPLE = '#6C4DFF';
const IMAGE_STYLES = [
  {
    id: 'bw_sketch',
    label: 'B&W Sketch',
    description: 'Black and white hand-drawn look.',
    image: '/imageSamples/BlackAndWhite.png',
  },
  {
    id: 'color_sketch',
    label: 'Color Sketch',
    description: 'Loose, colorful sketch style.',
    image: '/imageSamples/Color.png',
  },
  {
    id: 'cinematic',
    label: 'Photo Realistic',
    description: 'More realistic, cinematic frames.',
    image: '/imageSamples/Cinematic.png',
  },
];

function ProjectSettingsModal({ open, onClose, initialSettings, onSaved }) {
  const { script } = useContext(ScriptContext);
  const [imageStyle, setImageStyle] = useState('bw_sketch');
  const [orientation, setOrientation] = useState('landscape');
  const [timePeriod, setTimePeriod] = useState('Present');
  const [saving, setSaving] = useState(false);

  // Lock style if any images exist for any shots
  const hasAnyGeneratedImages = !!script?.scenes?.some((scene) =>
    (scene?.shot_list || []).some((shot) => !!shot?.image),
  );

  const styleLocked = !!initialSettings?.image_style && hasAnyGeneratedImages;

  // Sync local state when opened / initialSettings changes
  useEffect(() => {
    if (!open) return;

    if (initialSettings) {
      if (initialSettings.image_style) setImageStyle(initialSettings.image_style);
      if (initialSettings.image_orientation) setOrientation(initialSettings.image_orientation);
      if (typeof initialSettings.time_period === 'string') {
        setTimePeriod(initialSettings.time_period);
      }
    }
  }, [open, initialSettings]);

  const handleSave = async () => {
    if (!script.id || saving) return;

    const payload = {
      image_style: imageStyle,
      image_orientation: orientation,
      time_period: (timePeriod || '').trim(),
    };

    try {
      setSaving(true);
      await projectSettings.save(script.id, payload);
      toast.success('Settings saved. We will now generate your images for this scenes.');

      if (typeof onSaved === 'function') {
        onSaved(payload);
      }

      onClose();
    } catch (err) {
      console.error('Error saving project settings', err);
      toast.error('Unable to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          padding: 2,
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          pb: 0.5,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Box className="fullWidthContainer">
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Before we start Generating images
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
            Configure your image generation settings to create visuals for your shotlist. Image
            generation is free for now, but may require payment or limits in the future. Don’t
            worry—we’ll give you advance notice. For now, enjoy unlimited access!
          </Typography>
        </Box>

        <IconButton size="small" onClick={onClose} disabled={saving} sx={{ ml: 1 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Body */}
      <DialogContent sx={{ mt: 2 }}>
        {/* Generation Style */}
        <Box
          sx={{
            mb: 3,
            p: styleLocked ? 2 : 0,
            borderRadius: styleLocked ? 3 : 0,
            backgroundColor: styleLocked ? '#F5F5F7' : 'transparent',
            border: styleLocked ? '1px solid #E0E0E0' : 'none',
            transition: '0.2s ease',
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
            Generation Style
          </Typography>

          {styleLocked && (
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
              This project’s generation style is currently locked. To change the generation style
              delete all existing images first.
            </Typography>
          )}

          <Grid container spacing={2}>
            {IMAGE_STYLES.map((style) => {
              const selected = imageStyle === style.id;
              const lockedAndSelected = styleLocked && selected;

              return (
                <Grid item xs={12} sm={4} key={style.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      borderWidth: 2,
                      borderColor: selected ? PURPLE : 'transparent',
                      boxShadow: selected ? '0 0 0 1px rgba(108,77,255,0.2)' : 1,
                      position: 'relative',
                      transition: 'all 0.15s ease-out',
                      overflow: 'hidden',
                      backgroundColor: lockedAndSelected ? '#EFEFF2' : '#fff',
                      opacity: lockedAndSelected ? 0.9 : 1,
                    }}
                  >
                    {selected && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: PURPLE,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          zIndex: 2,
                        }}
                      >
                        <CheckCircleIcon sx={{ fontSize: 18 }} />
                      </Box>
                    )}

                    <CardActionArea
                      onClick={styleLocked ? undefined : () => setImageStyle(style.id)}
                      sx={{
                        borderRadius: 3,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        cursor: styleLocked ? 'not-allowed' : 'pointer',
                        pointerEvents: styleLocked ? 'none' : 'auto',
                      }}
                    >
                      {/* Image preview */}
                      <Box sx={{ height: 140, backgroundColor: '#f2f2f2', position: 'relative' }}>
                        <Box
                          component="img"
                          src={style.image}
                          alt={`${style.label} preview`}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      </Box>

                      <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {style.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {style.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Image Orientation */}
        <Box sx={{ mb: 3 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
              Image Orientation
            </FormLabel>
            <RadioGroup row value={orientation} onChange={(e) => setOrientation(e.target.value)}>
              <FormControlLabel value="landscape" control={<Radio />} label="Landscape" />
              <FormControlLabel value="vertical" control={<Radio />} label="Vertical" />
            </RadioGroup>
          </FormControl>
        </Box>

        {/* Time period of the action */}
        <Box>
          <FormControl fullWidth>
            <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
              Time period of the action
            </FormLabel>
            <TextField
              placeholder="Present"
              size="medium"
              value={timePeriod}
              onChange={(e) => setTimePeriod(e.target.value)}
              inputProps={{ maxLength: 100 }}
            />
          </FormControl>
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 2,
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={onClose}
          disabled={saving}
          variant="outlined"
          sx={{
            mr: 1.5,
            borderRadius: '999px',
            textTransform: 'none',
            borderColor: '#D0CCFF',
            color: '#666',
            backgroundColor: '#F9F8FF',
            '&:hover': {
              backgroundColor: '#F1EEFF',
              borderColor: PURPLE,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || !script.id}
          variant="contained"
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            px: 4,
            backgroundColor: PURPLE,
            '&:hover': {
              backgroundColor: '#5c3ae0',
            },
          }}
        >
          Next
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ProjectSettingsModal;
